import React, { useRef, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import { Search, X } from "lucide-react";
import { DataGrid } from "../DataGrid";
import {
  Card,
  CardFooter,
  CardHeader,
  CardHeading,
  CardTable,
  CardTitle,
  CardToolbar,
} from "../Card";
import { Input } from "../Input";
import { Button } from "../Button";
import { ScrollArea, ScrollBar } from "../ScrollArea";
import { DataGridTable } from "../DataGridTable";
import { PaginationComponent } from "components/atoms";
import { DataGridPagination } from "../DataGridPagination";

const TableContainer = ({
  columns,
  data,
  title = "",
  isPagination = true,
  recordCount = null,
  isGlobalFilter = true,
  onSearch = () => {},
  onUpdatePagination = () => {},
  onUpdateSort = () => {},
  extraButton = <></>,
  hasSearch = false,
  totalPages,
  paginationState = { pageIndex: 0, pageSize: 10 },
  customSorting = false,
  isFooter = true,
}) => {
  const [pagination, setPagination] = useState(
    !isPagination ? paginationState : { pageIndex: 0, pageSize: 10 },
  );

  const [sorting, setSorting] = useState([{ id: "lastModified", desc: true }]);

  // Local search state
  const [searchQuery, setSearchQuery] = useState("");
  const debounceRef = useRef(null);

  // Clear search when parent's search becomes null/empty
  useEffect(() => {
    if (paginationState?.search === null || paginationState?.search === "") {
      setSearchQuery("");
    }
  }, [paginationState?.search]);

  const table = useReactTable({
    columns,
    data: data,
    pageCount: Math.ceil(
      (recordCount ? recordCount : data?.length ? data?.length : 0) /
        paginationState?.pageSize,
    ),
    getRowId: (row) => row.id,
    state: {
      pagination: !isPagination ? paginationState : pagination,
      sorting,
    },
    columnResizeMode: "onChange",
    onSortingChange: !isPagination
      ? (updater) => {
          setSorting(updater);

          const nextSort =
            typeof updater === "function" ? updater(sorting) : updater;

          if (customSorting) {
            if (nextSort.length > 0) {
              const sortedColumn = columns.find(
                (col) => col.id === nextSort[0]?.id,
              );
              if (sortedColumn?.enableSorting) {
                onUpdateSort(nextSort);
              }
            } else {
              const lastSortedColumn = columns.find(
                (col) => col.id === sorting?.[0]?.id,
              );

              if (lastSortedColumn?.enableSorting) {
                onUpdateSort([]);
              }
            }
          }
        }
      : setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: !isPagination,
    manualSorting: true,
  });

  return (
    <DataGrid
      table={table}
      recordCount={recordCount ? recordCount : data?.length ? data?.length : 0}
      tableLayout={{
        columnsPinnable: true,
        columnsMovable: true,
        columnsVisibility: false,
        cellBorder: true,
      }}
    >
      <Card className="min-w-full">
        <CardHeader className="py-3 flex-wrap gap-2">
          <CardHeading>
            <CardTitle>
              <div className="flex gap-3 items-center z-0">
                {title && title}
                {hasSearch && (
                  <div className="absolute">
                    <Search className=" size-4  z-10 text-muted-foreground absolute start-3 top-1/2 -translate-y-1/2" />
                    <Input
                      placeholder={`Search ${title.toLowerCase()}`}
                      value={searchQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchQuery(value);

                        if (debounceRef.current)
                          clearTimeout(debounceRef.current);

                        debounceRef.current = setTimeout(() => {
                          if (onSearch) onSearch(value);
                        }, 1000);
                      }}
                      inputClassName="px-8"
                      className="w-50"
                    />
                    {searchQuery.length > 0 && (
                      <Button
                        mode="icon"
                        variant="ghost"
                        className="absolute end-1.5 top-1/2 -translate-y-1/2 h-6 w-6"
                        onClick={() => {
                          if (onSearch) {
                            onSearch("");
                          }
                          setSearchQuery("");
                        }}
                      >
                        <X />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardTitle>
          </CardHeading>
          <CardToolbar>
            {isGlobalFilter && (
              <div className="relative">
                <Search className="size-4 text-muted-foreground absolute start-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Search by ID"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ps-9 w-40"
                />

                {searchQuery.length > 0 && (
                  <Button
                    mode="icon"
                    variant="ghost"
                    className="absolute end-1.5 top-1/2 -translate-y-1/2 h-6 w-6"
                    onClick={() => {
                      setSearchQuery("");
                      if (onSearch) onSearch("");
                    }}
                  >
                    <X />
                  </Button>
                )}
              </div>
            )}
            {extraButton}
          </CardToolbar>
        </CardHeader>
        <CardTable>
          <ScrollArea>
            <DataGridTable />
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </CardTable>
        {isFooter && (
          <CardFooter>
            {isPagination ? (
              <DataGridPagination />
            ) : (
              <PaginationComponent
                limit={paginationState?.pageSize}
                onChangePagination={(size, page) => {
                  onUpdatePagination(size, page);
                }}
                total={recordCount}
                totalPage={totalPages}
              />
            )}
          </CardFooter>
        )}
      </Card>
    </DataGrid>
  );
};

export default TableContainer;
